import type { ConnectionChannel } from '@qm/contracts/connection-groups';
export const channelName = (c: ConnectionChannel, locale: 'en' | 'ar'): string =>
  ({
    facebook: locale === 'ar' ? 'صفحات Facebook' : 'Facebook Pages',
    instagram: locale === 'ar' ? 'Instagram المرتبط' : 'Linked Instagram',
    meta: 'Meta Ads',
    gbp: 'Google Business Profile',
    google_search_console: 'Google Search Console',
    google_ads: 'Google Ads',
  })[c];
const en = {
  title: 'Connect your accounts',
  intro:
    'Connect each provider once, then choose the business accounts to include. You can also use different logins for individual services.',
  same: 'Use the same account',
  separate: 'Use different accounts',
  separateHint: 'Authorize each service separately using the account that manages it.',
  sharedHint:
    'One authorization for your selected services. You choose the business assets afterwards.',
  metaHint:
    'Connect Facebook Pages and their linked professional Instagram accounts. Only assets you can access will be offered.',
  googleHint:
    'Connect Business Profile and Search Console together. Choose the profiles and websites to include.',
  ads: 'Include advertising accounts (optional)',
  connect: 'Connect',
  connected: 'Connected',
  pending: 'Needs attention',
  unconnected: 'Not connected',
  statusUnknown: 'Status unavailable — retry',
  noAutomatic:
    'Connecting does not publish content or start spending. Data appears only after a successful sync.',
  choose: 'Choose business accounts',
  chooseHint: 'Select the assets you want in this workspace. Unselected assets are not connected.',
  missing_permission: 'Permission not granted',
  no_accounts: 'No accessible accounts found',
  provider_error: 'Could not load this service',
  available: 'Ready to select',
  save: 'Connect selected accounts',
  back: 'Back to account setup',
  working: 'Checking your authorization…',
  failed: 'Could not complete this connection. Try again or use separate accounts.',
  partial:
    'Some accounts could not be connected. Review the results below; you can reconnect those services separately.',
  success: 'Selected accounts connected',
  notVerified: 'Property is not verified',
  none: 'No accounts were selected. Choose at least one available account.',
  readiness: 'Permissions and availability are checked separately for each service.',
  results: 'Connection results',
  next: 'Continue',
  chooseServices: 'Services to connect',
  inAppTitle: 'Open this page in Safari or Chrome to connect',
  inAppBody:
    'You are in an app’s built-in browser (for example Outlook or Facebook). Meta often blocks sign-in here. Copy the link, open it in Safari or Chrome, sign in and connect from there.',
  inAppCopy: 'Copy link',
  inAppCopied: 'Link copied. Paste it into Safari or Chrome.',
  inAppManual: 'Copy this link and open it in Safari or Chrome:',
  inAppContinue: 'Continue anyway',
  metaDenied:
    'The sign-in was cancelled or the permissions were declined, so nothing was connected. Try again and approve the requested permissions.',
  metaPermissions:
    'The sign-in did not grant the permissions this connection needs. Try again and keep every requested permission switched on.',
  metaUnavailable:
    'The provider is not accepting this sign-in right now. Try again later; if it keeps happening, contact Quixo support.',
};
const ar: typeof en = {
  title: 'اربط حساباتك',
  intro:
    'اربط كل مزوّد مرة واحدة، ثم اختر حسابات الأعمال التي تريد إضافتها. يمكنك أيضاً استخدام حسابات تسجيل دخول مختلفة لكل خدمة.',
  same: 'استخدام الحساب نفسه',
  separate: 'استخدام حسابات مختلفة',
  separateHint: 'امنح الإذن لكل خدمة بشكل منفصل باستخدام الحساب الذي يديرها.',
  sharedHint: 'تفويض واحد للخدمات التي تختارها. ستختار أصول الأعمال بعد ذلك.',
  metaHint:
    'اربط صفحات Facebook وحسابات Instagram الاحترافية المرتبطة بها. ستظهر فقط الأصول التي يمكنك الوصول إليها.',
  googleHint: 'اربط ملف النشاط التجاري وSearch Console معاً، ثم اختر الأنشطة والمواقع المطلوبة.',
  ads: 'إضافة حسابات الإعلانات (اختياري)',
  connect: 'ربط',
  connected: 'متصل',
  pending: 'يحتاج إلى إجراء',
  unconnected: 'غير متصل',
  statusUnknown: 'الحالة غير متاحة — أعد المحاولة',
  noAutomatic: 'الربط لا ينشر محتوى ولا يبدأ أي إنفاق. تظهر البيانات بعد نجاح المزامنة فقط.',
  choose: 'اختر حسابات الأعمال',
  chooseHint: 'اختر الأصول التي تريد إضافتها إلى مساحة العمل. لن يتم ربط الأصول غير المحددة.',
  missing_permission: 'لم يُمنح الإذن',
  no_accounts: 'لم يتم العثور على حسابات متاحة',
  provider_error: 'تعذّر تحميل هذه الخدمة',
  available: 'جاهز للاختيار',
  save: 'ربط الحسابات المحددة',
  back: 'العودة إلى إعداد الحسابات',
  working: 'جارٍ التحقق من التفويض…',
  failed: 'تعذّر إكمال الربط. أعد المحاولة أو استخدم حسابات منفصلة.',
  partial: 'تعذّر ربط بعض الحسابات. راجع النتائج أدناه؛ يمكنك إعادة ربط تلك الخدمات بشكل منفصل.',
  success: 'تم ربط الحسابات المحددة',
  notVerified: 'لم يتم التحقق من ملكية الموقع',
  none: 'لم تختر أي حساب. اختر حساباً متاحاً واحداً على الأقل.',
  readiness: 'يتم التحقق من الأذونات والتوفر لكل خدمة بشكل مستقل.',
  results: 'نتائج الربط',
  next: 'متابعة',
  chooseServices: 'الخدمات المطلوب ربطها',
  inAppTitle: 'افتح هذه الصفحة في Safari أو Chrome لإتمام الربط',
  inAppBody:
    'أنت تستخدم المتصفح المدمج في تطبيق (مثل Outlook أو Facebook). غالباً ما تمنع Meta تسجيل الدخول هنا. انسخ الرابط وافتحه في Safari أو Chrome، ثم سجّل الدخول وأكمل الربط من هناك.',
  inAppCopy: 'نسخ الرابط',
  inAppCopied: 'تم نسخ الرابط. الصقه في Safari أو Chrome.',
  inAppManual: 'انسخ هذا الرابط وافتحه في Safari أو Chrome:',
  inAppContinue: 'المتابعة على أي حال',
  metaDenied: 'أُلغي تسجيل الدخول أو رُفضت الأذونات، لذلك لم يُربط أي شيء. أعد المحاولة ووافق على الأذونات المطلوبة.',
  metaPermissions: 'لم يمنح تسجيل الدخول الأذونات التي يحتاجها هذا الربط. أعد المحاولة وأبقِ جميع الأذونات المطلوبة مفعّلة.',
  metaUnavailable: 'لا يقبل المزوّد تسجيل الدخول هذا حالياً. أعد المحاولة لاحقاً، وإذا تكرر الأمر فتواصل مع دعم Quixo.',
};
export const groupCopy = (locale: 'en' | 'ar') => (locale === 'ar' ? ar : en);
